import type { Config } from 'dompurify';
import { getApplicationSettings } from 'owa-application-settings';

/**
 * This list of safe protocols is used to prevent XSS attacks by only allowing certain protocols in href and src attributes.
 *
 * This list changes so infrequently (only once in few years) that it's safe to hardcode it, must match the list in
 * https://o365exchange.visualstudio.com/O365%20Core/_git/Substrate?path=/sources/dev/CTS/src/Server/TextConverters/Html/UrlSchemaChecker.cs
 *
 */
const defaultSafeProtocols = [
    'about',
    'blob',
    'callto',
    'cid',
    'codeflow',
    'conf',
    'file',
    'ftp',
    'gopher',
    'groove',
    'http',
    'https',
    'im',
    'ipp',
    'mailto',
    'meet',
    'mhtml',
    'mms',
    'msdaipp',
    'ms-excel',
    'ms-powerpoint',
    'ms-word',
    'ms-outlook',
    'news',
    'notes',
    'onenote',
    'sip',
    'tel',
    'wais',
] as const;

// Centralized default config for the sanitizer
const defaultAddAttr = ['target', 'originalsrc', 'summary', 'name', 'content'];
const defaultTrustedTypesConfig: Config = {
    USE_PROFILES: { html: true },
    // These attributes are allowed on every HTML element
    ADD_ATTR: defaultAddAttr,
};

let cachedAllowedUriRegExp: RegExp | undefined;

/**
 * Builds (and caches) the ALLOWED_URI_REGEXP from the default safe-protocol
 * list plus any server-configured schemas.
 */
function getAllowedUriRegExp(): RegExp {
    if (!cachedAllowedUriRegExp) {
        const applicationSettings = getApplicationSettings('UrlValidationSettings');
        const safeProtocols = [...defaultSafeProtocols, ...applicationSettings.whiteListedSchemas];
        cachedAllowedUriRegExp = new RegExp(
            `^(?:(?:${safeProtocols.join('|')}):|[^a-z]|[a-z+.\\-]+(?:[^a-z+.\\-:]|$))`,
            'i'
        );
    }
    return cachedAllowedUriRegExp;
}

const getGlobalSanitizerConfig = (extendedConfig: Config): Config => {
    // Merge USE_PROFILES
    const USE_PROFILES = {
        ...defaultTrustedTypesConfig.USE_PROFILES,
        ...extendedConfig.USE_PROFILES,
    };

    // Merge ADD_ATTR and remove duplicates
    const extendedAddAttr = Array.isArray(extendedConfig.ADD_ATTR) ? extendedConfig.ADD_ATTR : [];
    const ADD_ATTR = Array.from(new Set([...defaultAddAttr, ...extendedAddAttr]));

    // Build the protocol-restricted config without reassigning the input parameter
    const protocolConfig: Config = extendedConfig.ALLOW_UNKNOWN_PROTOCOLS
        ? extendedConfig
        : { ...extendedConfig, ALLOWED_URI_REGEXP: getAllowedUriRegExp() };

    return {
        ...defaultTrustedTypesConfig,
        ...protocolConfig,
        USE_PROFILES,
        ADD_ATTR,
    };
};

export default getGlobalSanitizerConfig;
